import { Badge, Button } from "@drs-ops/ui";
import { ConfirmDialog, EmptyState, LoadingSpinner } from "@drs-ops/ui/feedback";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { useState } from "react";
import { CrewAddEditDialog } from "@/components/settings/CrewAddEditDialog";
import { formatDistanceToNow } from "@/components/settings/time";
import { useAdminApi } from "@/hooks/useAdminApi";
import type { CrewMember } from "@/types/crewMember";
import type { CrewSyncOutcome, CrewSyncState } from "@/types/crewSync";

// `!= null`: a row read before 0002_qbo_crew has no qbo_employee_id at all, and is manual.
const isQbo = (m: CrewMember): boolean => m.qbo_employee_id != null;

// A guard skip still records a successful run, so say what it held back. A skipped roster
// also skips addresses ("roster_skipped"); the roster line covers both.
function skipWarnings(last: CrewSyncOutcome | null | undefined): string[] {
  if (!last?.ok) return [];
  if (last.rosterSkipped) return ["Roster not updated: QuickBooks returned too few employees."];
  if (last.addressSkipped === "addresses_failed") return ["Addresses not updated: the QuickBooks address read failed."];
  if (last.addressSkipped) return ["Addresses not updated: QuickBooks returned too few addresses."];
  return [];
}

export function CrewTab(): React.ReactElement {
  const api = useAdminApi();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["crew"],
    queryFn: () => api.get<CrewMember[]>("/api/crew"),
  });
  const syncState = useQuery({
    queryKey: ["crew-sync"],
    queryFn: () => api.get<CrewSyncState>("/api/crew/sync"),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CrewMember | undefined>(undefined);
  const [confirmTarget, setConfirmTarget] = useState<CrewMember | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (values: {
      name: string;
      email?: string | undefined;
      phone?: string | undefined;
      home_address?: string | undefined;
    }) => {
      const body = {
        name: values.name,
        email: values.email && values.email.length > 0 ? values.email : null,
        phone: values.phone && values.phone.length > 0 ? values.phone : null,
        home_address: values.home_address && values.home_address.length > 0 ? values.home_address : null,
      };
      if (editing) {
        await api.patch<CrewMember>(`/api/crew/${editing.id}`, body);
      } else {
        await api.post<CrewMember>("/api/crew", body);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["crew"] });
      setDialogOpen(false);
      setEditing(undefined);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (member: CrewMember) =>
      api.patch<CrewMember>(`/api/crew/${member.id}/deactivate`, {
        is_active: member.is_active !== 1,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["crew"] });
      setConfirmTarget(null);
    },
  });

  // QuickBooks roster (#168): run the sync now. The Worker enforces a 60 s cooldown and
  // answers with the last outcome inside it, so a double click is harmless.
  const refreshMutation = useMutation({
    mutationFn: () => api.post("/api/crew/sync"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["crew"] });
      void qc.invalidateQueries({ queryKey: ["crew-sync"] });
    },
  });

  const sync = syncState.data;
  const warnings = skipWarnings(sync?.last);
  const addressesUnread = sync?.last?.ok === true && sync.last.addressSkipped !== null;
  const syncHeader = (
    <div className="flex items-center justify-between gap-3">
      <div className="text-sm text-muted-foreground">
        <p>
          {sync?.lastSuccessAt
            ? `Last synced from QuickBooks ${formatDistanceToNow(sync.lastSuccessAt)}`
            : "Not yet synced from QuickBooks"}
          {sync?.lastError && <span className="ml-2 text-destructive">Last sync failed: {sync.lastError}</span>}
        </p>
        {warnings.map((w) => (
          <p key={w} className="text-destructive">
            {w}
          </p>
        ))}
      </div>
      <Button variant="outline" onClick={() => refreshMutation.mutate()} disabled={refreshMutation.isPending}>
        Refresh from QuickBooks
      </Button>
    </div>
  );

  if (list.isLoading) return <LoadingSpinner className="my-12" />;
  const rows = list.data ?? [];
  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {syncHeader}
        <EmptyState
          icon={Users}
          title="No crew yet"
          description="Add your first crew member to get started."
          action={
            <Button
              onClick={() => {
                setEditing(undefined);
                setDialogOpen(true);
              }}
            >
              Add crew member
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {syncHeader}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{rows.length} crew member(s)</p>
        <Button
          onClick={() => {
            setEditing(undefined);
            setDialogOpen(true);
          }}
        >
          Add crew member
        </Button>
      </div>
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50 text-left">
            <tr>
              <th className="p-2">Name</th>
              <th className="p-2">Email</th>
              <th className="p-2">Phone</th>
              <th className="p-2">Home address</th>
              <th className="p-2">Source</th>
              <th className="p-2">Status</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((m) => (
              <tr key={m.id} className="border-t border-border">
                <td className="p-2 font-medium">{m.name}</td>
                <td className="p-2">{m.email ?? "—"}</td>
                <td className="p-2">{m.phone ?? "—"}</td>
                <td className="p-2">
                  {m.home_address ??
                    (isQbo(m) ? (
                      <span className="text-muted-foreground">
                        {addressesUnread ? "Address not loaded from QuickBooks" : "No address in QuickBooks"}
                      </span>
                    ) : (
                      "—"
                    ))}
                </td>
                <td className="p-2">
                  <Badge variant="outline">{isQbo(m) ? "QuickBooks" : "Manual"}</Badge>
                </td>
                <td className="p-2">
                  <Badge variant={m.is_active ? "default" : "secondary"}>{m.is_active ? "Active" : "Inactive"}</Badge>
                </td>
                <td className="p-2 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditing(m);
                      setDialogOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  {!isQbo(m) && (
                    <Button size="sm" variant="ghost" className="ml-2" onClick={() => setConfirmTarget(m)}>
                      {m.is_active ? "Deactivate" : "Reactivate"}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CrewAddEditDialog
        open={dialogOpen}
        initial={editing}
        managed={editing !== undefined && isQbo(editing)}
        onCancel={() => setDialogOpen(false)}
        onSubmit={async (v) => {
          await saveMutation.mutateAsync(v);
        }}
        submitting={saveMutation.isPending}
      />

      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.is_active ? "Deactivate crew member?" : "Reactivate crew member?"}
        message={
          confirmTarget?.is_active
            ? "They'll be hidden from crew selectors but historical data is preserved."
            : "They'll reappear in crew selectors."
        }
        variant={confirmTarget?.is_active ? "danger" : "default"}
        confirmLabel={confirmTarget?.is_active ? "Deactivate" : "Reactivate"}
        onConfirm={() => confirmTarget && deactivateMutation.mutate(confirmTarget)}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
}
